/**
 * Queue and flush Google Drive deletes for this account's CachedMedia backups.
 * Never deletes files the signed-in user does not own.
 */
import localforage from 'localforage'

const PENDING_DELETE_KEY = 'media_cache_pending_drive_deletes'

const pendingStore = localforage.createInstance({
  name: 'abc2book',
  storeName: 'media_cache_drive_backup',
})

let registeredDriveApi = null

export function registerCachedMediaDriveApi(driveApi) {
  registeredDriveApi = driveApi || null
}

async function readPendingDriveDeletes() {
  const list = await pendingStore.getItem(PENDING_DELETE_KEY)
  return Array.isArray(list) ? list.filter(Boolean).map(String) : []
}

export async function listPendingCachedMediaDriveDeletes() {
  return readPendingDriveDeletes()
}

async function writePendingDriveDeletes(ids) {
  const unique = []
  const seen = {}
  ;(ids || []).forEach(function(id) {
    const key = String(id || '')
    if (!key || seen[key]) return
    seen[key] = true
    unique.push(key)
  })
  await pendingStore.setItem(PENDING_DELETE_KEY, unique)
  return unique
}

export async function clearCachedMediaDriveDeleteQueue() {
  await pendingStore.removeItem(PENDING_DELETE_KEY)
}

export async function enqueueCachedMediaDriveDeletes(fileIds) {
  const incoming = (fileIds || []).filter(Boolean).map(String)
  if (!incoming.length) return await readPendingDriveDeletes()
  const list = await readPendingDriveDeletes()
  incoming.forEach(function(id) {
    if (list.indexOf(id) < 0) list.push(id)
  })
  return writePendingDriveDeletes(list)
}

function errorStatus(err) {
  if (!err) return 0
  if (err.response && err.response.status) return Number(err.response.status) || 0
  if (err.status) return Number(err.status) || 0
  return 0
}

function isOwnerDenied(err) {
  const status = errorStatus(err)
  return status === 403 || status === 404
}

async function isOwnedByMe(driveApi, fileId) {
  if (!driveApi || typeof driveApi.getDocumentMeta !== 'function') return true
  try {
    const meta = await driveApi.getDocumentMeta(fileId)
    if (!meta) return false
    if (meta.ownedByMe === false) return false
    if (meta.ownedByMe === true) return true
    return true
  } catch (e) {
    return false
  }
}

export async function flushCachedMediaDriveDeletes(driveApi, options) {
  const api = driveApi || registeredDriveApi
  const opts = options || {}
  if (!api || typeof api.deleteDocument !== 'function') {
    return { deleted: 0, remaining: await readPendingDriveDeletes(), skipped: 0 }
  }
  const accessToken = opts.token && opts.token.access_token
    ? opts.token.access_token
    : opts.accessToken
  if (!accessToken) {
    return { deleted: 0, remaining: await readPendingDriveDeletes(), skipped: 0 }
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { deleted: 0, remaining: await readPendingDriveDeletes(), skipped: 0 }
  }

  const pending = await readPendingDriveDeletes()
  if (!pending.length) return { deleted: 0, remaining: [], skipped: 0 }

  const remaining = []
  let deleted = 0
  let skipped = 0
  for (let i = 0; i < pending.length; i += 1) {
    const fileId = pending[i]
    try {
      const owned = await isOwnedByMe(api, fileId)
      if (!owned) {
        skipped += 1
        continue
      }
      const result = await api.deleteDocument(fileId)
      if (result && result.error) {
        if (isOwnerDenied(result.error)) {
          skipped += 1
        } else {
          remaining.push(fileId)
        }
      } else {
        deleted += 1
      }
    } catch (e) {
      if (isOwnerDenied(e)) {
        skipped += 1
      } else {
        remaining.push(fileId)
      }
    }
  }
  await writePendingDriveDeletes(remaining)
  return { deleted: deleted, remaining: remaining, skipped: skipped }
}

export async function enqueueAndTryFlushCachedMediaDriveDeletes(fileIds, options) {
  await enqueueCachedMediaDriveDeletes(fileIds)
  return flushCachedMediaDriveDeletes(options && options.driveApi, options || {})
}
